import { useEffect } from 'react';
import { useRouter } from 'next/router';
import type { ContactFormFields } from '@transcend-io/contact-form-schema';
import { snakeCase } from 'change-case';
import { AnimatePresence, motion } from 'framer-motion';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  FormProvider,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';
import type { ZodType } from 'zod';

import type { ResponseError } from '#/lib/api/types';
import {
  ChiliPiperProvider,
  useChiliPiperModal,
} from '#/lib/chili-piper/ChiliPiperContext';
import { scheduleAfterSubmit } from '#/lib/chili-piper/scheduleAfterSubmit';
import { checkFormServerError } from '#/lib/contact-form-client/helpers';
import { submitLead } from '#/lib/contact-form-client/submit';
import type { SanityDefaultForm } from '#/lib/sanity/queries/form';
import { useAnalytics, useConversionEvent } from '#/lib/segment';

import { generateAnalyticsId, useFormMetadata } from './utils';

type Props<T extends ZodType<ContactFormFields>> = {
  hubspotCampaignId?: SanityDefaultForm['hubspotCampaignId'];
  hubspotConvertingFormType?: SanityDefaultForm['hubspotConvertingFormType'];
  contentPieceTitle?: SanityDefaultForm['contentPieceTitle'];
  hubspotEmailSubscriptionIds?: SanityDefaultForm['hubspotEmailSubscriptionIds'];
  chiliPiperRouterId?: string | null;
  formFieldsSchema: T;
  trackingEventName: SanityDefaultForm['trackingEventName'];
  trackingEventProperties: SanityDefaultForm['trackingEventProperties'];
  ConfirmationComponent: React.FC;
  onSubmitSuccess?: () => void;
  onSubmitError?: ({ error }: ResponseError) => void;
  formTitle: SanityDefaultForm['formTitle'];
  /**
   * The form interior
   *
   * This should at a minimum have render logic for these states:
   * - Form is submitting: `ctx.formState.isSubmitting`
   * - Server error: `formState.errors.root?.type === 'server'` (see `checkFormServerError` helper)
   */
  children: (ctx: UseFormReturn<ContactFormFields>) => React.ReactNode;
};

function FormInner<T extends ZodType<ContactFormFields>>({
  hubspotCampaignId,
  hubspotConvertingFormType,
  contentPieceTitle,
  hubspotEmailSubscriptionIds,
  chiliPiperRouterId,
  formFieldsSchema,
  trackingEventName,
  trackingEventProperties,
  ConfirmationComponent,
  onSubmitSuccess,
  onSubmitError,
  children,
  formTitle,
}: Props<T>) {
  const { asPath } = useRouter();

  const { methods, getMetadata } = useFormMetadata({
    hubspotCampaignId,
    hubspotConvertingFormType,
    contentPieceTitle,
    hubspotEmailSubscriptionIds,
    trackingEventName,
    formFieldsSchema,
    formTitle,
    path: asPath,
  });

  const fireConversionEvent = useConversionEvent();
  const analytics = useAnalytics();
  const { initSchedulingModal, waitForSchedulingFinished } =
    useChiliPiperModal();

  // Consent to be contacted is implied in the contact form
  useEffect(() => methods.setValue('consent', true), [methods]);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit: SubmitHandler<ContactFormFields> = async (fields) => {
    try {
      // Yield to paint submitting UI before recaptcha / Segment. Fire
      // conversion after a successful submit (same order as Multi).
      const recaptchaToken =
        executeRecaptcha &&
        (await executeRecaptcha(snakeCase(trackingEventName)));

      const metadata = await getMetadata(recaptchaToken);

      const response = await submitLead(fields, metadata, { analytics });

      // Server responded with an error
      if ('error' in response) {
        console.error(response.error);
        methods.setError('root', {
          type: 'server',
          message: response.error,
        });
        const serverError = checkFormServerError(methods.formState);
        if (serverError) {
          onSubmitError?.({
            error: serverError.message,
          });
        }
      } else {
        if (chiliPiperRouterId) {
          await scheduleAfterSubmit({
            fields,
            submissionId: response.submissionId,
            analyticsId: generateAnalyticsId(formTitle, asPath),
            trackingEventName,
            analytics,
            getMetadata,
            executeRecaptcha,
            initSchedulingModal,
            waitForSchedulingFinished,
          });
        }
        void fireConversionEvent(
          fields.email,
          trackingEventName,
          trackingEventProperties,
        );
        onSubmitSuccess?.();
      }
    } catch (err) {
      // Something else went wrong
      console.error(err);
      methods.setError('root', {
        type: 'server',
      });
      const serverError = checkFormServerError(methods.formState);
      if (serverError) {
        onSubmitError?.({
          error: serverError.message,
        });
      }
    }
  };

  return (
    <div className="relative">
      <AnimatePresence mode="popLayout">
        {methods.formState.isSubmitSuccessful ? (
          <motion.div
            key="confirmation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ConfirmationComponent />
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <FormProvider {...methods}>
              <form
                name={generateAnalyticsId(formTitle, asPath)}
                onSubmit={methods.handleSubmit(handleSubmit)}
              >
                {children(methods)}
              </form>
            </FormProvider>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Form<T extends ZodType<ContactFormFields>>(props: Props<T>) {
  const chiliPiperRouterId = props.chiliPiperRouterId?.trim() || null;

  return (
    <ChiliPiperProvider
      enableScheduling={Boolean(chiliPiperRouterId)}
      routerId={chiliPiperRouterId}
    >
      <FormInner {...props} chiliPiperRouterId={chiliPiperRouterId} />
    </ChiliPiperProvider>
  );
}
